import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Loader2,
  Trash2,
  ArrowUpDown
} from 'lucide-react';
import AdminLayout from '../AdminLayout';
import { getAllTransactions } from '../../api/transaction';
import { deleteTransaction } from '../../api/transaction';
import { getUsers } from '../../api/auth';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deletingTxId, setDeletingTxId] = useState(null);

  // Fetch transactions and users
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch transactions and users in parallel
        const [txResponse, usersResponse] = await Promise.all([
          getAllTransactions(),
          getUsers()
        ]);
        
        if (txResponse.transactions) {
          setTransactions(txResponse.transactions);
        }
        if (usersResponse.users) {
          setUsers(usersResponse.users);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load transactions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? (user.username || user.email || user.phone || 'Unknown User') : userId;
  };

  // Sort transactions
  const sortedTransactions = [...transactions].sort((a, b) => {
    let aVal = a[sortBy] || '';
    let bVal = b[sortBy] || '';
    
    if (sortBy === 'amount') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else if (sortBy === 'created_at') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleDelete = async (txid) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    try {
      setDeletingTxId(txid);
      const response = await deleteTransaction(txid);
      if (response.success || response.message) {
        // Remove transaction from list
        setTransactions(transactions.filter(tx => tx.txid !== txid));
        alert('✅ Transaction deleted successfully');
      }
    } catch (err) {
      console.error('Error deleting transaction:', err);
      alert(`❌ Failed to delete transaction: ${err.response?.data?.error || err.message || 'Unknown error'}`);
    } finally {
      setDeletingTxId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4 lg:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-800">All Transactions</h2>
            <p className="text-xs lg:text-sm text-gray-600 mt-1">{sortedTransactions.length} transactions found</p>
          </div>
        </div>

        {/* Transactions Table - Desktop */}
        <Card className="overflow-hidden hidden lg:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('from_user_id')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                    >
                      From User <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('to_merchant_id')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                    >
                      To Merchant <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('amount')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                    >
                      Amount <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                    >
                      Status <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('created_at')}
                      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase hover:text-gray-700"
                    >
                      Created <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delete</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#586BFF] mx-auto" />
                      <p className="text-sm text-gray-500 mt-2">Loading transactions...</p>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <p className="text-red-500 mb-2">Error loading transactions</p>
                      <p className="text-sm text-gray-500">{error}</p>
                    </td>
                  </tr>
                ) : sortedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <p className="text-gray-500">No transactions found</p>
                    </td>
                  </tr>
                ) : (
                  sortedTransactions.map((transaction) => (
                    <tr key={transaction.txid} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-800 font-medium">
                          {getUserName(transaction.from_user_id)}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">{transaction.from_user_id?.substring(0, 8)}...</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-800 font-medium">
                          {getUserName(transaction.to_merchant_id)}
                        </p>
                        <p className="text-xs text-gray-500 font-mono">{transaction.to_merchant_id?.substring(0, 8)}...</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-800">₹{transaction.amount?.toLocaleString() || '0'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : transaction.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-800">{formatDate(transaction.created_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          onClick={() => handleDelete(transaction.txid)}
                          disabled={deletingTxId === transaction.txid}
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 h-7 text-xs px-3 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingTxId === transaction.txid ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Deleting...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3 h-3" />
                              <span>Delete</span>
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Transactions Cards - Mobile */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <Card className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#586BFF] mx-auto" />
              <p className="text-sm text-gray-500 mt-2">Loading transactions...</p>
            </Card>
          ) : error ? (
            <Card className="p-6 text-center">
              <p className="text-red-500 mb-2">Error loading transactions</p>
              <p className="text-sm text-gray-500">{error}</p>
            </Card>
          ) : sortedTransactions.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-gray-500">No transactions found</p>
            </Card>
          ) : (
            sortedTransactions.map((transaction) => (
              <Card key={transaction.txid} className="p-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500">From User</p>
                      <p className="text-sm font-semibold text-gray-800">{getUserName(transaction.from_user_id)}</p>
                      <p className="text-xs text-gray-400 font-mono">{transaction.from_user_id?.substring(0, 8)}...</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">To Merchant</p>
                      <p className="text-sm font-semibold text-gray-800">{getUserName(transaction.to_merchant_id)}</p>
                      <p className="text-xs text-gray-400 font-mono">{transaction.to_merchant_id?.substring(0, 8)}...</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center pt-2 border-t">
                    <div>
                      <p className="text-xs text-gray-500">Amount</p>
                      <p className="text-lg font-bold text-gray-800">₹{transaction.amount?.toLocaleString() || '0'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Status</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        transaction.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : transaction.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.status || 'unknown'}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-xs text-gray-500">{formatDate(transaction.created_at)}</p>
                    <Button
                      onClick={() => handleDelete(transaction.txid)}
                      disabled={deletingTxId === transaction.txid}
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 h-7 text-xs px-3 flex items-center gap-1 disabled:opacity-50"
                    >
                      {deletingTxId === transaction.txid ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-3 h-3" />
                          <span>Delete</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Transactions;

